import { FC } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { LeftSide, SettingBox, StyledForm, Subtitle, WhiteWrapper } from '../styles';
import { SmallerChip } from '@/components/Tag/styles';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import CloseIcon from '@mui/icons-material/Close';
import { getTagName, areTagsEqual } from '@/utils/tagUtils';
import ActionDialog from '@/components/ActionDialog';
import { useManageTags } from './useManageTags';

const ManageTags: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    isLoading,
    tags,
    tagCounts,
    openDeleteDialog,
    openEditDialog,
    openMergeDialog,
    selectedTag,
    newTagName,
    tagsToMerge,
    setNewTagName,
    setTagsToMerge,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog,
    handleDeleteTag,
    handleOpenEditDialog,
    handleCloseEditDialog,
    handleEditTag,
    handleOpenMergeDialog,
    handleCloseMergeDialog,
    handleMergeTags,
  } = useManageTags();

  return (
    <SettingBox>
      <LeftSide>
        <Typography variant="body1" fontWeight="700">
          Manage Tags
        </Typography>
        <Subtitle variant="body2">
          Edit, delete, or merge your tags. Changes will affect all notes that use these tags.
        </Subtitle>
      </LeftSide>
      <StyledForm>
        <Stack>
          <WhiteWrapper>
            {tags.length === 0 ? (
              <Typography variant="body2">You don't have any tags yet.</Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={isMobile ? 0.5 : 1} mb={2}>
                {tags
                  .filter((tag) => tag && typeof tag === 'string')
                  .map((tag) => (
                    <SmallerChip
                      key={tag}
                      label={`${tag} (${tagCounts[tag] ?? 0})`}
                      onDelete={() => handleOpenDeleteDialog(tag)}
                      deleteIcon={<CloseIcon fontSize="small" />}
                      onClick={() => handleOpenEditDialog(tag)}
                      sx={{
                        height: isMobile ? '28px' : '24px',
                        margin: isMobile ? '2px' : 0,
                        '& .MuiChip-label': {
                          fontSize: isMobile ? '11px' : '12px',
                          padding: isMobile ? '4px 6px' : '4px 8px',
                        },
                        '& .MuiChip-deleteIcon': {
                          color: 'inherit',
                          fontSize: isMobile ? '14px' : '16px',
                        },
                      }}
                    />
                  ))}
              </Stack>
            )}

            <Stack spacing={1} mt={2}>
              <Typography variant="body2" fontWeight="700">
                Actions
              </Typography>
              {tags.length > 1 && (
                <Button
                  variant="outlined"
                  startIcon={<MergeTypeIcon />}
                  onClick={() => handleOpenMergeDialog()}
                  disabled={isLoading}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  Merge Tags
                </Button>
              )}
            </Stack>

            {isLoading && (
              <Stack alignItems="center" mt={2} spacing={1}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Updating tags across all your notes...
                </Typography>
              </Stack>
            )}
          </WhiteWrapper>
        </Stack>
      </StyledForm>

      {/* Delete Tag Dialog */}
      <ActionDialog
        open={openDeleteDialog}
        title="Delete Tag"
        content={`Are you sure you want to delete the tag "${selectedTag}"? This will remove the tag from all your notes.`}
        variant="danger"
        confirmText="Delete"
        onConfirm={handleDeleteTag}
        onCancel={handleCloseDeleteDialog}
        loading={isLoading}
        ariaLabelledBy="delete-dialog-title"
        ariaDescribedBy="delete-dialog-description"
      />

      {/* Edit Tag Dialog */}
      <ActionDialog
        open={openEditDialog}
        title="Edit Tag"
        content={`Edit the tag "${selectedTag}". This will update the tag across all your notes.`}
        variant="form"
        confirmText="Save"
        onConfirm={handleEditTag}
        onCancel={handleCloseEditDialog}
        loading={isLoading}
        ariaLabelledBy="edit-dialog-title"
        ariaDescribedBy="edit-dialog-description"
      >
        <TextField
          autoFocus
          margin="dense"
          label="New Tag Name"
          type="text"
          fullWidth
          variant="outlined"
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
          disabled={isLoading}
        />
      </ActionDialog>

      {/* Merge Tags Dialog */}
      <ActionDialog
        open={openMergeDialog}
        title="Merge Tags"
        content="Select multiple tags to merge into a single tag. This will update all notes with any of these tags."
        variant="form"
        maxWidth="sm"
        confirmText="Merge"
        onConfirm={handleMergeTags}
        onCancel={handleCloseMergeDialog}
        loading={isLoading}
        confirmDisabled={tagsToMerge.length < 2 || !newTagName.trim()}
        ariaLabelledBy="merge-dialog-title"
        ariaDescribedBy="merge-dialog-description"
      >
        <Stack spacing={2}>
          <Autocomplete
            multiple
            freeSolo
            filterSelectedOptions
            disableCloseOnSelect
            options={tags}
            value={tagsToMerge}
            onChange={(_, value) => {
              // Ensure all values are strings, extracting name if object
              const stringValues = value.map((v) => getTagName(v));
              setTagsToMerge(stringValues);
            }}
            getOptionLabel={(option) => getTagName(option)}
            isOptionEqualToValue={(option, value) => areTagsEqual(option, value)}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                margin="dense"
                label="Tags to merge"
                placeholder="Type or select tags"
                fullWidth
                disabled={isLoading}
                helperText="Choose or type multiple tags (hit Enter or click to add)"
              />
            )}
          />
          <TextField
            margin="dense"
            label="New Merged Tag Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            placeholder="Enter the name for the merged tag"
            disabled={isLoading}
          />
        </Stack>
      </ActionDialog>
    </SettingBox>
  );
};

export default ManageTags;
